// src/types/badges.ts
// This file is now aligned with YOUR real endpoints:
//
//   GET /api/badges   → BadgeApi[]
//   GET /api/profile  → { ..., badges: ProfileBadgeApi[] }
//
// Everything below just turns those into the shape the UI wants.

//
// ----- RAW API SHAPES -----
//

export interface BadgeLevelApi {
  badgeLevelId: number;
  badgeId: string; // backend sends all-zeros here, so we won’t rely on it
  levelName: string; // "Bronze" | "Silver" | "Gold" | "Sapphire" ...
  levelNumber: number; // 1,2,3,4
  levelDescription: string;
  levelImageUrl?: string;
  levelPointsRequired: number;
  createdAt: string;
  studentBadgeAccounts: any[];
}

export interface BadgeApi {
  badgeId: string;
  badgeName: string;
  badgeSlug: string;
  badgeDescription?: string;
  badgeImageUrl?: string;
  badgeDuration: number;
  publicRanking: boolean;
  createdAt: string;
  badgeLevels: BadgeLevelApi[];
  badgeXpEvents: any[];
  studentBadgeAccounts: any[];
  acornBadgeXps: any[];
}

// this is the thing inside /api/profile.badges[]
export interface ProfileBadgeApi {
  badgeName: string;
  badgeDescription?: string;
  badgeId: string;
  currentXp: number;
  levelNumber: number;
  levelName: string;
  badgeImageUrl?: string;
  levelImageUrl?: string;
}

//
// ----- UI SHAPES -----
//

// the fully-normalized badge definition used by the app
export interface BadgeDefinition {
  id: string; // ← always api.badgeId
  name: string; // ← always api.badgeName
  slug: string; // ← always api.badgeSlug
  category: string;
  description: string;
  badgeImageUrl?: string;
  // e.g. { Bronze: 10, Silver: 25, Gold: 50, Sapphire: 100 }
  levelThresholds: Record<string, number>;
  // e.g. { Bronze: '/badges/1.1...', ... }
  levelImages: Record<string, string>;
  // UI color
  color: string;
}

export interface StudentBadgeProgress {
  badgeId: string;
  badgeName: string;
  currentXP: number; // note the capital P in the UI
  levelNumber: number;
  currentLevel: string;
  badgeImageUrl?: string;
  levelImageUrl?: string;
}

export interface Badge extends BadgeDefinition {
  currentLevel: string; // 'none' | 'Bronze' | 'Silver' | ...
  currentXP: number;
}

// Optional nominal type for a badge level label used in UI calculations
export type BadgeLevel = string; // e.g., 'bronze' | 'silver' | 'gold' | 'sapphire' | ...

// XP activity history item used by UI components and hooks
export type ActivityType = 'award' | 'redeem';

export interface Activity {
  id: string;
  type: ActivityType;
  amount: number;
  reason: string;
  timestamp: Date;
  awardedBy?: {
    id: string;
    name: string;
    // Keep flexible: role values can vary by feature (teacher, student, admin, system, ...)
    role: string;
  };
}

//
// ----- TRANSFORMERS -----
//

const DEFAULT_BADGE_COLOR = '#4f46e5'; // indigo

// turn GET /api/badges item → BadgeDefinition (UI)
export const mapBadgeApiToDefinition = (api: BadgeApi): BadgeDefinition => {
  const levelThresholds: Record<string, number> = {};
  const levelImages: Record<string, string> = {};

  // your backend sends badgeLevels as array; we’ll sort by levelNumber
  (api.badgeLevels ?? [])
    .slice()
    .sort((a, b) => a.levelNumber - b.levelNumber)
    .forEach((lvl) => {
      levelThresholds[lvl.levelName] = lvl.levelPointsRequired;
      if (lvl.levelImageUrl) {
        levelImages[lvl.levelName] = lvl.levelImageUrl;
      }
    });

  return {
    id: api.badgeId,
    name: api.badgeName,
    slug: api.badgeSlug,
    // simple category, since API doesn’t give one
    category: api.badgeSlug?.replace(/-/g, ' ') ?? 'badge',
    description: api.badgeDescription ?? '',
    badgeImageUrl: api.badgeImageUrl,
    levelThresholds,
    levelImages,
    color: DEFAULT_BADGE_COLOR,
  };
};

// turn /api/profile.badges[] item → StudentBadgeProgress (UI)
export const mapProfileBadgeToProgress = (
  api: ProfileBadgeApi
): StudentBadgeProgress => {
  return {
    badgeId: api.badgeId,
    badgeName: api.badgeName,
    currentXP: api.currentXp, // ← profile uses currentXp
    levelNumber: api.levelNumber,
    currentLevel: api.levelName,
    badgeImageUrl: api.badgeImageUrl,
    levelImageUrl: api.levelImageUrl,
  };
};

// convenience: merge a definition with *one* student badge
export const mergeDefinitionWithStudent = (
  def: BadgeDefinition,
  student: StudentBadgeProgress | undefined
): Badge => {
  return {
    ...def,
    currentLevel: student?.currentLevel ?? 'none',
    currentXP: student?.currentXP ?? 0,
  };
};
